<template>
  <main class="news-admin">
    <section class="news-admin__panel">
      <p class="news-admin__eyebrow">AJL Notes CMS</p>
      <h1>Notes verwalten</h1>

      <div v-if="!isFirebaseConfigured" class="news-admin__notice">
        Firebase ist noch nicht konfiguriert. Bitte die
        <code>VUE_APP_FIREBASE_*</code> Werte in <code>.env</code> setzen.
      </div>

      <form v-else-if="!user" class="news-admin__form" @submit.prevent="login">
        <label>
          E-mail
          <input v-model="loginForm.email" type="email" required />
        </label>
        <label>
          Passwort
          <input v-model="loginForm.password" type="password" required />
        </label>
        <button type="submit">Einloggen</button>
        <p v-if="errorMessage" class="news-admin__error">{{ errorMessage }}</p>
      </form>

      <template v-else>
        <div class="news-admin__toolbar">
          <span>{{ user.email }}</span>
          <button type="button" @click="logout">Logout</button>
        </div>

        <form class="news-admin__form" @submit.prevent="saveNews">
          <label>
            Titel
            <input v-model="form.title" type="text" required />
          </label>
          <label>
            Datum
            <input v-model="form.date" type="date" required />
          </label>
          <label>
            Autor
            <input v-model="form.author" type="text" required />
          </label>
          <label>
            Kurztext
            <textarea v-model="form.excerpt" rows="3" required></textarea>
          </label>
          <section class="news-admin__editor-field">
            <span>Volltext</span>
            <div ref="bodyEditor" class="news-admin__editor"></div>
          </section>
          <label>
            Bild-URL
            <input
              v-model="form.imageUrl"
              type="url"
              placeholder="https://..."
            />
          </label>
          <label class="news-admin__checkbox">
            <input v-model="form.published" type="checkbox" />
            Veröffentlicht
          </label>
          <div v-if="form.imageUrl" class="news-admin__preview">
            <img :src="form.imageUrl" :alt="form.title || 'Notes image'" />
          </div>
          <div class="news-admin__actions">
            <button type="submit" :disabled="isSaving">
              {{ form.id ? "Aktualisieren" : "Erstellen" }}
            </button>
            <button type="button" @click="resetForm">Neu</button>
          </div>
          <p v-if="errorMessage" class="news-admin__error">{{ errorMessage }}</p>
        </form>

        <section class="news-admin__list">
          <h2>Bestehende Notes</h2>
          <article
            v-for="item in newsItems"
            :key="item.id"
            :class="{ 'news-admin__list-item--no-image': !item.imageUrl }"
          >
            <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title" />
            <div>
              <p class="news-admin__date">{{ item.displayDate }}</p>
              <p class="news-admin__author">By {{ item.author || "Rasha Corti" }}</p>
              <h3>{{ item.title }}</h3>
              <p>{{ item.excerpt }}</p>
              <strong>{{ item.published ? "Published" : "Draft" }}</strong>
            </div>
            <div class="news-admin__item-actions">
              <button type="button" @click="editNews(item)">Edit</button>
              <button type="button" @click="removeNews(item.id)">Delete</button>
            </div>
          </article>
        </section>
      </template>
    </section>
  </main>
</template>

<script>
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { exec as pellExec, init as initPell } from "pell";
import "pell/dist/pell.min.css";
import { auth, db, isFirebaseConfigured } from "../firebase";
import { formatNewsDate } from "../news";

const emptyForm = () => ({
  id: "",
  title: "",
  date: new Date().toISOString().slice(0, 10),
  author: "Rasha Corti",
  excerpt: "",
  body: "",
  imageUrl: "",
  published: true,
});

export default {
  name: "NewsAdmin",
  data() {
    return {
      editor: null,
      errorMessage: "",
      form: emptyForm(),
      isFirebaseConfigured,
      isSaving: false,
      loginForm: {
        email: "",
        password: "",
      },
      newsItems: [],
      unsubscribeAuth: null,
      user: null,
    };
  },
  mounted() {
    document.title = "AJL Notes CMS";
    this.setNoIndexMeta();
    if (!isFirebaseConfigured) {
      return;
    }

    this.unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      this.user = user;
      if (user) {
        this.$nextTick(this.initEditor);
        this.loadNews();
      }
    });
  },
  beforeUnmount() {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
    this.removeNoIndexMeta();
  },
  methods: {
    setNoIndexMeta() {
      let tag = document.head.querySelector('meta[name="robots"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "robots");
        tag.setAttribute("data-admin-noindex", "true");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", "noindex,nofollow");
    },
    removeNoIndexMeta() {
      const tag = document.head.querySelector('meta[data-admin-noindex="true"]');
      if (tag) {
        tag.remove();
      }
    },
    async login() {
      this.errorMessage = "";
      try {
        await signInWithEmailAndPassword(
          auth,
          this.loginForm.email,
          this.loginForm.password
        );
      } catch (error) {
        this.errorMessage = error.message;
      }
    },
    async logout() {
      await signOut(auth);
      this.newsItems = [];
      this.editor = null;
      this.resetForm();
    },
    async loadNews() {
      const newsQuery = query(collection(db, "news"), orderBy("date", "desc"));
      const snapshot = await getDocs(newsQuery);
      this.newsItems = snapshot.docs.map((document) => {
        const data = document.data();
        return {
          id: document.id,
          ...data,
          dateInput: this.toDateInput(data.date),
          displayDate: formatNewsDate(data.date),
        };
      });
    },
    async saveNews() {
      this.errorMessage = "";
      this.isSaving = true;
      this.normalizeEditorBody();

      try {
        const payload = {
          body: this.normalizeBodyHtml(this.form.body),
          author: this.form.author || "Rasha Corti",
          date: Timestamp.fromDate(new Date(`${this.form.date}T12:00:00`)),
          excerpt: this.form.excerpt,
          imageUrl: this.form.imageUrl,
          published: this.form.published,
          title: this.form.title,
          updatedAt: serverTimestamp(),
        };

        if (this.form.id) {
          await updateDoc(doc(db, "news", this.form.id), payload);
        } else {
          await addDoc(collection(db, "news"), {
            ...payload,
            createdAt: serverTimestamp(),
          });
        }

        this.resetForm();
        await this.loadNews();
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isSaving = false;
      }
    },
    editNews(item) {
      this.form = {
        id: item.id,
        title: item.title,
        date: item.dateInput || new Date().toISOString().slice(0, 10),
        author: item.author || "Rasha Corti",
        excerpt: item.excerpt,
        body: item.body || "",
        imageUrl: item.imageUrl || "",
        published: item.published,
      };
      this.$nextTick(this.setEditorContent);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async removeNews(id) {
      if (!window.confirm("Diese Note wirklich löschen?")) {
        return;
      }

      await deleteDoc(doc(db, "news", id));
      await this.loadNews();
    },
    resetForm() {
      this.form = emptyForm();
      this.$nextTick(this.setEditorContent);
    },
    initEditor() {
      if (!this.$refs.bodyEditor || this.editor) {
        return;
      }

      this.editor = initPell({
        element: this.$refs.bodyEditor,
        defaultParagraphSeparator: "p",
        styleWithCSS: false,
        actions: [
          "paragraph",
          "heading1",
          "heading2",
          "bold",
          "italic",
          "underline",
          "ulist",
          {
            name: "link",
            result: () => {
              const url = window.prompt("Link URL");
              if (url) {
                pellExec("createLink", url);
              }
            },
          },
        ],
        onChange: (html) => {
          this.form.body = html;
        },
      });

      this.editor
        .querySelectorAll(".pell-button")
        .forEach((button) =>
          button.addEventListener("mousedown", (event) => event.preventDefault())
        );
      this.setEditorContent();
    },
    setEditorContent() {
      if (this.editor?.content) {
        this.editor.content.innerHTML = this.form.body || "";
      }
    },
    normalizeEditorBody() {
      const normalizedHtml = this.normalizeBodyHtml(this.form.body);
      this.form.body = normalizedHtml;
      this.setEditorContent();
    },
    normalizeBodyHtml(html) {
      if (!html) {
        return "";
      }

      const template = document.createElement("template");
      template.innerHTML = html
        .replace(/&nbsp;/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim();

      template.content
        .querySelectorAll("script, style, meta, iframe, object, embed")
        .forEach((node) => node.remove());

      template.content.querySelectorAll("*").forEach((node) => {
        [...node.attributes].forEach((attribute) => {
          if (
            attribute.name.startsWith("on") ||
            attribute.name === "style" ||
            attribute.name === "class"
          ) {
            node.removeAttribute(attribute.name);
          }
        });
      });

      template.content.querySelectorAll("div").forEach((node) => {
        const paragraph = document.createElement("p");
        paragraph.innerHTML = node.innerHTML;
        node.replaceWith(paragraph);
      });

      template.content.querySelectorAll("p, h1, h2, li").forEach((node) => {
        node.innerHTML = node.innerHTML.replace(/\s{2,}/g, " ").trim();
      });

      template.content.querySelectorAll("p").forEach((node) => {
        const text = node.textContent.replace(/\u00a0/g, " ").trim();
        if (!text && !node.querySelector("img")) {
          node.remove();
        }
      });

      return template.innerHTML.trim();
    },
    toDateInput(value) {
      if (!value) {
        return new Date().toISOString().slice(0, 10);
      }

      const date = typeof value.toDate === "function" ? value.toDate() : value;
      return date.toISOString().slice(0, 10);
    },
  },
};
</script>

<style scoped>
.news-admin {
  background: #f6f8fb;
  min-height: 100vh;
  padding: 48px 16px;
}

.news-admin__panel {
  background: #ffffff;
  color: #20324f;
  margin: 0 auto;
  max-width: 980px;
  padding: 32px;
}

.news-admin__eyebrow,
.news-admin__date {
  color: #13a1c7;
  font-family: "Poppins", sans-serif;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.news-admin h1 {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 28px;
}

.news-admin__notice,
.news-admin__error {
  background: rgba(19, 161, 199, 0.1);
  padding: 14px;
}

.news-admin__error {
  color: #a32929;
}

.news-admin__form {
  display: grid;
  gap: 18px;
}

.news-admin label {
  display: grid;
  gap: 8px;
  font-weight: 800;
}

.news-admin__editor-field {
  display: grid;
  gap: 8px;
  font-weight: 800;
}

.news-admin input,
.news-admin textarea {
  border: 1px solid rgba(32, 50, 79, 0.25);
  font: inherit;
  padding: 10px 12px;
}

.news-admin__editor {
  border: 1px solid rgba(32, 50, 79, 0.25);
}

.news-admin__editor :deep(.pell-actionbar) {
  border: 0;
  border-bottom: 1px solid rgba(32, 50, 79, 0.18);
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
}

.news-admin__editor :deep(.pell-button) {
  background: #f6f8fb;
  border: 1px solid rgba(32, 50, 79, 0.14);
  color: #20324f;
  font-family: "Poppins", sans-serif;
  font-size: 0.78rem;
  font-weight: 800;
  min-width: 36px;
  padding: 8px 10px;
}

.news-admin__editor :deep(.pell-button-selected),
.news-admin__editor :deep(.pell-button:hover) {
  background: #13a1c7;
  color: #ffffff;
}

.news-admin__editor :deep(.pell-content) {
  color: #20324f;
  font-family: "Playfair Display", serif;
  font-size: 1.02rem;
  font-weight: 400;
  line-height: 1.55;
  min-height: 180px;
  padding: 16px;
}

.news-admin__editor :deep(.pell-content:focus) {
  border-color: #13a1c7;
  outline: 2px solid rgba(19, 161, 199, 0.18);
}

.news-admin__editor :deep(.pell-content h1) {
  color: #13a1c7;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.12;
  margin: 0 0 8px;
}

.news-admin__editor :deep(.pell-content h2) {
  color: #20324f;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 8px;
}

.news-admin__editor :deep(.pell-content p) {
  margin: 0 0 8px;
}

.news-admin__editor :deep(.pell-content ul) {
  list-style: disc;
  margin: 0 0 8px 22px;
  padding-left: 0;
}

.news-admin__editor :deep(.pell-content li) {
  margin: 0 0 4px;
}

.news-admin__editor :deep(.pell-content strong),
.news-admin__editor :deep(.pell-content b) {
  color: #20324f;
  font-weight: 800;
}

.news-admin__editor :deep(.pell-content em),
.news-admin__editor :deep(.pell-content i) {
  font-style: italic;
}

.news-admin__editor :deep(.pell-content u) {
  text-decoration: underline;
  text-decoration-color: #13a1c7;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.news-admin__editor :deep(.pell-content a) {
  color: #13a1c7;
  font-weight: 800;
  text-decoration: underline;
}

.news-admin button {
  background: #13a1c7;
  color: #ffffff;
  font-family: "Poppins", sans-serif;
  font-weight: 800;
  padding: 12px 18px;
  text-transform: uppercase;
}

.news-admin button:hover {
  background: #20324f;
}

.news-admin__checkbox {
  align-items: center;
  display: flex;
}

.news-admin__toolbar,
.news-admin__actions {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 24px;
}

.news-admin__preview img,
.news-admin__list img {
  aspect-ratio: 16 / 9;
  object-fit: cover;
  width: 100%;
}

.news-admin__list {
  display: grid;
  gap: 18px;
  margin-top: 40px;
}

.news-admin__list h2 {
  font-size: 1.75rem;
  font-weight: 800;
}

.news-admin__list article {
  border-top: 1px solid rgba(19, 161, 199, 0.35);
  display: grid;
  gap: 18px;
  grid-template-columns: 180px minmax(0, 1fr) auto;
  padding-top: 18px;
}

.news-admin__list-item--no-image {
  grid-template-columns: minmax(0, 1fr) 180px !important;
}

.news-admin__list h3 {
  font-size: 1.25rem;
  font-weight: 800;
  margin-bottom: 6px;
}

.news-admin__item-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.news-admin__author {
  font-family: "Poppins", sans-serif;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  margin: 0 0 8px;
  text-transform: uppercase;
}

@media (max-width: 767px) {
  .news-admin__panel {
    padding: 22px;
  }

  .news-admin__list article {
    grid-template-columns: 1fr;
  }

  .news-admin__toolbar,
  .news-admin__actions {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
